"use client";
import { motion } from "motion/react";
import { useAppSelector } from "../../_hooks/hooks";
import { useEffect, useState } from "react";
import { ICMS } from "../home/homeModel";
import { createLookupObject } from "../../_helper/helper";
import Link from "next/link";
import dynamic from "next/dynamic";

const StatsSection = dynamic(() => import("./components/StatsSection"));
const StorySection = dynamic(() => import("./components/StorySection"));
const ValuesSection = dynamic(() => import("./components/ValuesSection"));
const VisionSection = dynamic(() => import("./components/VisionSection"));

const Button = dynamic(() =>
  import("../../components/ui/button").then((mod) => mod.Button),
);

export default function About({ cmsData }: { cmsData?: any[] }) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});

  useEffect(() => {
    if (cmsData && cmsData?.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const banner: any =
    BannerList?.find(
      (banner) => banner?.BannerType === "about-kavish-hotels-and-resorts",
    ) || [];

  return (
    <>
      <div className="min-h-screen bg-white text-md">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-end justify-center overflow-hidden">
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url('${banner?.BannerImage}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 mt-25 mb-15">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {banner?.SubTitle && (
                <div className="inline-block px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
                  <p className="text-[#C9A962] tracking-[0.4em] text-sm">
                    {banner?.SubTitle || "OUR STORY"}
                  </p>
                </div>
              )}

              <h1 className="text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight max-w-5xl mx-auto font-playfair">
                <span className="text-[#C9A962]">
                  {banner?.BannerTitle ||
                    "We Don't Just Build Resorts, We Craft Experiences"}
                </span>
              </h1>

              {banner?.Description && (
                <p
                  className="text-white/95 text-xl md:text-2xl max-w-3xl mx-auto font-playfair"
                  dangerouslySetInnerHTML={{
                    __html: banner?.Description || "",
                  }}
                />
              )}
            </motion.div>
          </div>
        </section>

        {lookUpCMSData?.["About - Section 1 : Statistics"] && (
          <StatsSection
            statsHTML={
              lookUpCMSData?.["About - Section 1 : Statistics"]?.Template || ""
            }
          />
        )}

        {lookUpCMSData?.["About - Section 2 : The Beginning 1"] && (
          <StorySection
            storyHTML={
              lookUpCMSData?.["About - Section 2 : The Beginning 1"]
                ?.Template || ""
            }
            image1={
              lookUpCMSData?.["About - Section 2 : The Beginning 1"]
                ?.CMSImage || ""
            }
            image1Alt={
              lookUpCMSData?.["About - Section 2 : The Beginning 1"]
                ?.CMSImageAlterTag || ""
            }
            image2={
              lookUpCMSData?.["About - Section 2 : The Beginning 2"]
                ?.CMSImage || ""
            }
            image2Alt={
              lookUpCMSData?.["About - Section 2 : The Beginning 2"]
                ?.CMSImageAlterTag || ""
            }
          />
        )}

        {lookUpCMSData?.["About - Section 3 : Our Values"] && (
          <ValuesSection
            valuesHTML={
              lookUpCMSData?.["About - Section 3 : Our Values"]?.Template || ""
            }
          />
        )}

        {lookUpCMSData?.["About - Section 4 : Our Vision"] && (
          <VisionSection
            visionHTML={
              lookUpCMSData?.["About - Section 4 : Our Vision"]?.Template || ""
            }
            bgImage={
              lookUpCMSData?.["About - Section 4 : Our Vision"]?.CMSImage || ""
            }
          />
        )}

        {/* CTA */}
        <section className="py-10 md:py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {lookUpCMSData?.["About - Section 5 : CTA"] ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: lookUpCMSData?.["About - Section 5 : CTA"]?.Template,
                }}
              />
            ) : (
              <>
                <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                  Be Part of Our Story
                </h2>
                <p className="text-gray-600 text-xl max-w-2xl mx-auto mb-12">
                  Every guest who walks through our doors adds a new chapter.
                  What will yours be?
                </p>
              </>
            )}
            <Link href={"/"}>
              <Button
                size="lg"
                className="bg-[#B42025] hover:bg-[#8F1A1E] text-white px-12 py-7 text-lg"
              >
                Explore Our Resorts
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
